
import { PlusCircle, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Field, SelectField } from '~/components/forms';
import { getInputProps, getSelectProps, useField } from '@conform-to/react';
import { CategoryItem } from '~/.server/api/routers/workspace';
import { getCurrencyOptions } from '~/utils/workspace/currency';

type RentalType = {
  slug: string;
  priceMin?: number;
  priceMax?: number;
  currency: string;
};

type WorkspaceRentalTypesFieldProps = {
  name: string;
  rentalTypeOptions: CategoryItem[];
};

export function WorkspaceRentalTypesField({
  rentalTypeOptions,
  name,
}: WorkspaceRentalTypesFieldProps) {
  const [meta, form] = useField<RentalType[]>(name);
  const rentalTypesList = meta.getFieldList();
  // Get currency options
  const currencyOptions = getCurrencyOptions();

  // Get the list of already selected rental type slugs
  const selectedSlugs = rentalTypesList.map(field => {
    const fields = field.getFieldset();
    return fields.slug?.value || '';
  });
  
  // Filter out already selected options
  const availableOptions = rentalTypeOptions.filter(
    option => !selectedSlugs.includes(option.value)
  );

  return (
    <div className="space-y-4">
      {/* Render each rental type as a card */}
      {rentalTypesList.map((rentalTypeField, index) => {
        const fields = rentalTypeField.getFieldset();

        // Find the label for the selected slug
        const selectedOption = rentalTypeOptions.find(opt => opt.value === fields.slug?.value);
        
        return (
          <Card key={rentalTypeField.key} className="relative">
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-2 top-2"
              onClick={() => form.remove({ 
                name, 
                index 
              })}
              type="button"
            >
              <X className="h-4 w-4" />
            </Button>
            <CardContent className="p-4 pt-6">
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-4 sm:col-span-1">
                  <SelectField 
                    labelProps={{ 
                      htmlFor: fields.slug?.id || `rental-type-${index}-slug`, 
                      children: 'Tipo de alquiler' 
                    }}
                    selectProps={{
                      ...getSelectProps(fields.slug),
                      defaultValue: fields.slug?.value?.toString()
                    }}
                    options={[
                      // Include the currently selected option even if it's been selected elsewhere
                      ...(selectedOption ? [selectedOption] : []),
                      ...availableOptions
                    ]}
                    errors={fields.slug?.errors}
                  />
                </div>
                <div className="col-span-4 sm:col-span-1">
                  <SelectField 
                    labelProps={{ 
                      htmlFor: fields.currency?.id, 
                      children: 'Moneda' 
                    }}
                    selectProps={{
                      ...getSelectProps(fields.currency),
                      defaultValue: fields.currency?.value?.toString() || 'EUR'
                    }}
                    options={currencyOptions}
                    errors={fields.currency?.errors}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Field 
                    labelProps={{ 
                      htmlFor: fields.priceMin?.id, 
                      children: 'Precio desde' 
                    }}
                    inputProps={{
                      ...getInputProps(fields.priceMin, {type: 'number'}),
                    }}
                    errors={fields.priceMin?.errors}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Field 
                    labelProps={{ 
                      htmlFor: fields.priceMax?.id, 
                      children: 'Precio hasta' 
                    }}
                    inputProps={{
                      ...getInputProps(fields.priceMax, {type: 'number'}),
                    }}
                    errors={fields.priceMax?.errors}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {/* Button to add a new rental type */}
      {availableOptions.length > 0 && (
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => {
            form.insert({
              name: name,
            })
          }}
          className="flex items-center gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          Añadir tipo de alquiler
        </Button>
      )}
    </div>
  );
}
